/** My Drafts page — shows draft wiki entries authored by current editor. */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../auth/api';
import { FileEdit, Clock, Eye, Trash2 } from 'lucide-react';

export default function MyDrafts() {
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get('/wiki/drafts/mine');
                setDrafts(res.data || []);
            } catch (e) {
                setError('Failed to load drafts');
            }
            setLoading(false);
        };
        load();
    }, []);

    const deleteDraft = async (id) => {
        if (!confirm('Delete this draft?')) return;
        try {
            await api.delete(`/wiki/entry/${id}/draft`);
            setDrafts(drafts.filter(d => d.id !== id));
        } catch (e) { alert('Failed to delete draft'); }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-lg font-bold text-white">My Drafts</h1>
                <button onClick={() => navigate('/wiki/edit/new')} className="px-3 py-2 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-medium">
                    + New Draft
                </button>
            </div>

            {error && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>}

            {loading ? (
                <div className="text-slate-400 text-sm">Loading drafts...</div>
            ) : drafts.length === 0 ? (
                <div className="text-slate-400 text-sm">
                    No drafts yet. <Link to="/wiki/edit/new" className="text-amber-400 hover:underline">Create one</Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {drafts.map(d => (
                        <DraftCard key={d.id} draft={d} onEdit={() => navigate(`/wiki/edit/${d.id}`)} onDelete={() => deleteDraft(d.id)} onView={() => navigate(`/wiki/entry/${d.slug}`)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function DraftCard({ draft, onEdit, onDelete, onView }) {
    const fmtDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-white truncate">{draft.title_en || draft.title_vi || '(untitled)'}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        {draft.category_id && <span className="px-1.5 py-0.5 rounded bg-slate-700">{draft.category_id}</span>}
                        <span className="flex items-center gap-1"><Clock size={12} /> Updated {fmtDate(draft.updated_at)}</span>
                    </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button onClick={onView} className="p-1.5 rounded hover:bg-slate-700 text-slate-400" title="View">
                        <Eye size={16} />
                    </button>
                    <button onClick={onEdit} className="p-1.5 rounded hover:bg-slate-700 text-amber-400" title="Edit">
                        <FileEdit size={16} />
                    </button>
                    <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-500/20 text-red-400" title="Delete">
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}